import { Grid, CircularProgress } from "@mui/material";
import React, { useEffect, useState, useRef } from "react";
import StreamFailed from "../Wowza/utils/StreamFailed";
import EventBus from "orion-components/SharedComponents/EventBus";
import { getTranslation } from "orion-components/i18n";
import { cameraService } from "client-app-core";
import utils from "./utils";
import PropTypes from "prop-types";

// Constants
const RETRY_INTERVAL_MS = 200;
const STREAM_RETRY_DELAY_MS = 500;
const CONNECTION_TIMEOUT_SEC = 60;
const AVIGILON_SPEED_RANGE = [-5, -2, -1, -0.5, 0.5, 1, 2, 5];
const LIVE_FRAME_TIMEOUT_MS = 5000;
const RECORDED_FRAME_TIMEOUT_MS = 5000;

// Helper Functions
const isTargetInstance = (targetId, currentId) => targetId === currentId;

const createCenteredContainer = (children, style = {}) => (
	<div
		style={{
			height: "100%",
			width: "100%",
			display: "flex",
			justifyContent: "center",
			alignItems: "center",
			...style
		}}
	>
		{children}
	</div>
);

const propTypes = {
	camera: PropTypes.object,
	instanceId: PropTypes.string,
	containerRef: PropTypes.object,
	updateDimensions: PropTypes.func,
	isInFullScreen: PropTypes.bool,
	selectedVideoMode: PropTypes.string
};

const AvigilonWebRTCPlayer = ({
	camera,
	instanceId,
	containerRef,
	updateDimensions,
	isInFullScreen,
	selectedVideoMode
}) => {
	const videoElemRef = useRef(null);
	const lastStatusTimeRef = useRef(null);
	const isPageHiddenRef = useRef(typeof document !== "undefined" ? document.hidden : false);

	/**
	 * playerStatusRef.current.network: "connecting", "connected" and "disconnected"
	 * playerStatusRef.current.player: "playing", "paused", "stopped"
	 */
	const playerStatusRef = useRef({});

	// Recorded-mode refs
	const currentSpeedRef = useRef(1);
	const playbackStartedRef = useRef(false);
	const videoStartDateTimeRef = useRef(null);
	const videoEndDateTimeRef = useRef(null);
	const recordedModeRef = useRef("recorded");
	const loopSeekToggleRef = useRef(false);

	// Frame tracking refs
	const lastFrameTimeRef = useRef(null);
	const firstFrameReceivedRef = useRef(false);
	const frameCheckIntervalRef = useRef(null);
	// Skips the first isPlaying event after each play() call — the player transitions
	// to isPlaying before real video data arrives, so that first hit can be a false positive.
	const skipNextPlayingEventRef = useRef(false);
	const userPausedRef = useRef(false);
	const certCheckInProgressRef = useRef(false);

	// Ref to track current mode inside event handlers without stale closures
	const selectedVideoModeRef = useRef(selectedVideoMode);
	useEffect(() => {
		selectedVideoModeRef.current = selectedVideoMode;
	}, [selectedVideoMode]);

	const isNewVideoPlayer = camera.player?.newPlayer || false;

	const initializeConnectionPayload = () => {
		if (camera?.connection) {
			return {
				integration: camera?.connection?.integration || "acc",
				config: {
					username: camera?.connection?.username || null,
					password: camera?.connection?.password || null,
					nonce: camera?.connection?.nonce || null,
					secretKey: camera?.connection?.secretKey || null,
					clientName: camera?.connection?.clientName || null
				}
			};
		}
		return null;
	};

	const initializeBaseUrl = () => {
		return camera?.player?.url || camera?.connection?.baseUrl;
	};

	const [connectionPayload] = useState(initializeConnectionPayload());
	const [baseUrl] = useState(initializeBaseUrl());

	// The unity-player web component only reads width/height on initialization,
	// so we always use 100% and let the parent container (frame div in
	// LiveVideoPlayer) control the actual dimensions via CSS.
	const videoWidth = "100%";
	const videoHeight = "100%";

	const [isLoaded, setIsLoaded] = useState(false);
	const [streamStatus, setStreamStatus] = useState("Starting"); //Either "Starting", "Started" or "Failed".
	// null | "live-stall" | "recorded-no-start" | "recorded-stall"
	const [overlayType, setOverlayType] = useState(null);
	const [errorMessage, setErrorMessage] = useState(undefined);
	// true once frames are actively arriving; false during startup/reconnect/playback-restart windows
	const [framesActive, setFramesActive] = useState(false);

	useEffect(() => {
		const handleVisibilityChange = () => {
			const now = Date.now();
			isPageHiddenRef.current = document.hidden;
			lastStatusTimeRef.current = now;
			if (streamStatus === "Started") {
				lastFrameTimeRef.current = now;
			}
		};

		document.addEventListener("visibilitychange", handleVisibilityChange);
		return () => {
			document.removeEventListener("visibilitychange", handleVisibilityChange);
		};
	}, [streamStatus]);

	const checkStreamCertAndSetMessage = () => {
		if (certCheckInProgressRef.current) {
			return;
		}

		certCheckInProgressRef.current = true;
		cameraService.checkCertValid(baseUrl, (err, result) => {
			certCheckInProgressRef.current = false;

			if (err) {
				console.error("Error validating stream certificate:", err);
				setErrorMessage(undefined);
				return;
			}

			if (result?.isCertValid === false) {
				setErrorMessage(getTranslation("global.CBComponents.video.webRTCPlayer.certError"));
				return;
			}

			setErrorMessage(undefined);
		});
	};

	// Start recorded playback if both the player is connected and the time frame is set.
	// Called from both setPlayBackTimeFrame (when time frame arrives) and
	// onStateChanged (when player connects), so whichever happens second triggers playback.
	const startRecordedPlayback = () => {
		const videoElem = videoElemRef.current;
		const startDt = videoStartDateTimeRef.current;
		const endDt = videoEndDateTimeRef.current;
		if (!videoElem || !startDt || !endDt || playerStatusRef.current.network !== "connected") return false;
		if (playbackStartedRef.current) return false;
		playbackStartedRef.current = true;

		// Reset frame tracking for this playback session
		firstFrameReceivedRef.current = false;
		lastFrameTimeRef.current = Date.now();
		skipNextPlayingEventRef.current = true;
		userPausedRef.current = false;
		setFramesActive(false);
		setOverlayType(null);
		setErrorMessage(undefined);

		// The unity player starts in live mode on connect, so pause first,
		// seek to the recorded start, then play. The isLive check in
		// onStateChanged prevents live displayTime from reaching the timeline.
		videoElem.pause();
		videoElem.setPlaybackTime(startDt);
		console.debug(`[AvigilonPlayer][${instanceId}] setPlaybackTime to: ${new Date(startDt)}`);

		// Publish the start time to the timeline immediately so the cursor
		// shows the correct position rather than the live position.
		EventBus.publish("setCurrentRecordedTimestamp", { instanceId, time: new Date(startDt) });

		videoElem.play();
		EventBus.publish("RecordedBottomBar_startVideo", { instanceId, playing: true });

		// Publish player capabilities when starting recorded playback
		console.debug(
			`[AvigilonWebRTCPlayer][${instanceId}] Publishing capabilities (startRecordedPlayback):`,
			AVIGILON_SPEED_RANGE
		);
		EventBus.publish("VideoPlayer_CapabilitiesReady", {
			instanceId,
			supportedSpeeds: AVIGILON_SPEED_RANGE
		});

		return true;
	};

	const setPlayBackTimeFrame = value => {
		console.debug(
			`[AvigilonPlayer][${instanceId}] setPlayBackTimeFrame received, target=${value.instanceId}, match=${isTargetInstance(value.instanceId, instanceId)}`
		);
		if (!isTargetInstance(value.instanceId, instanceId)) {
			return;
		}
		const { startDateTimeVal, endDateTimeVal, recordedMode } = value;
		videoStartDateTimeRef.current = startDateTimeVal;
		videoEndDateTimeRef.current = endDateTimeVal;
		recordedModeRef.current = recordedMode || "recorded";

		// Allow re-start on new time frame (e.g. user changes the date range)
		playbackStartedRef.current = false;

		// Keep trying until startRecordedPlayback returns true
		const retryInterval = setInterval(() => {
			const success = startRecordedPlayback();
			if (success) {
				clearInterval(retryInterval);
			}
		}, RETRY_INTERVAL_MS);
	};

	const applyPlaybackCommand = commandObj => {
		console.debug(
			`[AvigilonPlayer][${instanceId}] applyPlaybackCommand received cmd=${commandObj.command}, target=${commandObj.instanceId}, match=${isTargetInstance(commandObj.instanceId, instanceId)}`
		);
		if (!isTargetInstance(commandObj.instanceId, instanceId)) {
			return;
		}
		const videoElem = videoElemRef.current;
		if (!videoElem) return;
		switch (commandObj.command) {
			case "pause":
				console.debug("[AvigilonPlayer] Applying recorded video command: pause");
				pauseVideo();
				break;
			case "play":
				console.debug("[AvigilonPlayer] Applying recorded video command: play");
				videoElem.changePlaybackSpeed(currentSpeedRef.current);
				playVideo(false); //continue playing
				break;
			case "setCursor": {
				const cursorEpoch = commandObj.time instanceof Date ? commandObj.time.getTime() : commandObj.time;
				console.debug(`[AvigilonPlayer] Applying recorded video command: setCursor (${cursorEpoch})`);

				// calling setPlaybackTime while playing will pause playback until stop moving scrubber (it won't actually scrub)
				// If paused it will sometimes scrub but very unreliable
				currentSpeedRef.current = 1;
				videoElem.setPlaybackTime(cursorEpoch);
				console.debug(`[AvigilonPlayer][${instanceId}] setPlaybackTime to: ${new Date(cursorEpoch)}`);

				// Update timeline cursor immediately for responsive UI
				EventBus.publish("setCurrentRecordedTimestamp", { instanceId, time: new Date(cursorEpoch) });
				break;
			}
			case "setSpeed": {
				//Note: Avigilon speed range is [-5, -2, -1, -0.5, 0.5, 1, 2, 5] only.
				console.debug(`[AvigilonPlayer] Applying recorded video command: setSpeed (${commandObj.speed})`);

				currentSpeedRef.current = commandObj.speed;
				videoElem.changePlaybackSpeed(currentSpeedRef.current);
				break;
			}
		}
	};

	// Mount: load scripts and set up player
	useEffect(() => {
		console.debug(`[AvigilonPlayer][${instanceId}] Mounting player for camera=${camera?.connection?.cameraId}`);
		utils.loadScripts();
		setIsLoaded(true);
		return () => {
			console.debug(
				`[AvigilonPlayer][${instanceId}] Unmounting player for camera=${camera?.connection?.cameraId}`
			);
		};
	}, []);

	// Subscribe/unsubscribe to recorded EventBus events based on mode
	useEffect(() => {
		if (selectedVideoMode !== "Live") {
			console.debug(`[AvigilonPlayer][${instanceId}] Entering recorded mode, subscribing to EventBus`);
			// Reset recorded state for fresh start
			playbackStartedRef.current = false;
			currentSpeedRef.current = 1;

			EventBus.subscribe("AvigilonRecordedWebRTCPlayer_applyPlaybackCommand", applyPlaybackCommand);
			EventBus.subscribe("AvigilonRecordedWebRTCPlayerWrapper_setPlayBackTimeFrame", setPlayBackTimeFrame);

			return () => {
				console.debug(`[AvigilonPlayer][${instanceId}] Leaving recorded mode, unsubscribing from EventBus`);
				EventBus.unsubscribe("AvigilonRecordedWebRTCPlayerWrapper_setPlayBackTimeFrame", setPlayBackTimeFrame);
				EventBus.unsubscribe("AvigilonRecordedWebRTCPlayer_applyPlaybackCommand", applyPlaybackCommand);
			};
		} else {
			// Switching to live mode: seek to time 0 (live) and resume playback
			const videoElem = videoElemRef.current;
			if (videoElem && playerStatusRef.current.network === "connected") {
				console.debug(`[AvigilonPlayer][${instanceId}] Switching to live mode, setPlaybackTime(0) + play()`);
				videoElem.setPlaybackTime(0);
				videoElem.play();
			}

			// Reset recorded refs
			videoStartDateTimeRef.current = null;
			videoEndDateTimeRef.current = null;
			playbackStartedRef.current = false;
			currentSpeedRef.current = 1;

			// Reset frame tracking for live mode
			firstFrameReceivedRef.current = false;
			lastFrameTimeRef.current = Date.now();
			skipNextPlayingEventRef.current = true;
			setFramesActive(false);
			setOverlayType(null);
			setErrorMessage(undefined);
		}
	}, [selectedVideoMode]);

	// Before interacting with the player, here we first establish a connection by invoking the `connect` method on the `UnityPlayer` instance.
	useEffect(() => {
		if (!isLoaded) return;
		const videoElem = videoElemRef.current;
		if (!videoElem) return;

		videoElem.currentCameraId = "";
		videoElem.cameraId = camera?.connection?.cameraId;
		videoElem.width = videoWidth;
		videoElem.height = videoHeight;

		// Workaround: unity-player's onStateChanged fires for ALL iframe
		// messages globally (no source filtering). Instead, listen directly
		// on window and filter by event.source to only handle our own iframe.
		const messageHandler = event => {
			const iframe = videoElem.iframe;
			if (!iframe || event.source !== iframe.contentWindow) return;
			const { header, payload } = event.data || {};
			if (header?.msgType === "state-changed") {
				getCurrentStateOfUnityPlayer({ detail: { payload } });
			}
		};
		window.addEventListener("message", messageHandler);

		// The unity-player web component creates its iframe asynchronously.
		// Poll until the iframe exists, then call connect(). We intentionally
		// do NOT check !iframeLoaded here — connect() has its own internal
		// polling that waits for the iframe to load before sending the
		// configure message. Checking !iframeLoaded is racy because the
		// iframe's load event can fire between React's commit phase and
		// this useEffect, causing tryConnect to never succeed.
		// This addresses issue where playback fails to start on initial load
		const tryConnect = () => {
			if (videoElem.iframe) {
				console.debug(
					`[AvigilonPlayer][${instanceId}] iframe ready, connecting. DOM id=${videoElem.id}, iframeLoaded=${videoElem.iframeLoaded}`
				);
				videoElem.connect(connectionPayload);
				return true;
			}
			return false;
		};

		let intervalId = null;
		if (!tryConnect()) {
			intervalId = setInterval(() => {
				if (tryConnect()) {
					clearInterval(intervalId);
					intervalId = null;
				}
			}, RETRY_INTERVAL_MS);
		}

		return () => {
			if (intervalId) clearInterval(intervalId);
			window.removeEventListener("message", messageHandler);
		};
	}, [isLoaded, videoElemRef.current]);

	// Start/stop the frame-timeout check interval whenever stream connection state changes.
	// For live: show error if no frame arrives within LIVE_FRAME_TIMEOUT_MS; clear when frames resume.
	// For recorded: show "no start" if no frame arrives after playback starts; show "stalled" if frames stall mid-playback.
	useEffect(() => {
		if (streamStatus !== "Started") return;

		// Seed the clock so we allow a full timeout window from connection time
		if (lastFrameTimeRef.current === null) {
			lastFrameTimeRef.current = Date.now();
		}

		frameCheckIntervalRef.current = setInterval(() => {
			const now = Date.now();
			const isRecorded = selectedVideoModeRef.current !== "Live";
			const isRecordedPaused = isRecorded && userPausedRef.current;

			if (isPageHiddenRef.current || isRecordedPaused) {
				lastStatusTimeRef.current = now;
				if (streamStatus === "Started") {
					lastFrameTimeRef.current = now;
				}
				return;
			}

			if (lastStatusTimeRef.current !== null && now - lastStatusTimeRef.current > CONNECTION_TIMEOUT_SEC * 1000) {
				console.error(
					`[AvigilonPlayer][${instanceId}] Connection timeout (${CONNECTION_TIMEOUT_SEC}s) - no status updates received for camera=${camera?.connection?.cameraId}`
				);
				setStreamStatus("Failed");
				checkStreamCertAndSetMessage();
				return;
			}

			if (!isRecorded) {
				if (now - lastFrameTimeRef.current > LIVE_FRAME_TIMEOUT_MS) {
					setOverlayType("live-stall");
				}
			} else {
				// Only check once playback has been initiated — avoids false positives
				// before startRecordedPlayback runs.
				if (!playbackStartedRef.current) return;
				if (!firstFrameReceivedRef.current) {
					if (now - lastFrameTimeRef.current > RECORDED_FRAME_TIMEOUT_MS) {
						setOverlayType("recorded-no-start");
					}
				} else if (
					!userPausedRef.current &&
					playerStatusRef.current.player === "playing" &&
					now - lastFrameTimeRef.current > RECORDED_FRAME_TIMEOUT_MS
				) {
					// Frames were flowing but stopped while player reports playing — stall
					setOverlayType("recorded-stall");
				}
			}
		}, 1000);

		return () => {
			clearInterval(frameCheckIntervalRef.current);
		};
	}, [streamStatus]);

	// Watchdog for the "Starting" state — if no state-changed message arrives within
	// CONNECTION_TIMEOUT_SEC seconds, the player is unlikely to connect (e.g. cert error).
	// Transition to "Failed" and run the cert check so a meaningful error is shown.
	useEffect(() => {
		if (streamStatus !== "Starting") return;

		// Seed the reference time so each new Starting cycle gets a fresh window.
		lastStatusTimeRef.current = Date.now();

		const watchdogInterval = setInterval(() => {
			const now = Date.now();
			if (isPageHiddenRef.current) {
				lastStatusTimeRef.current = now;
				return;
			}
			if (now - lastStatusTimeRef.current > CONNECTION_TIMEOUT_SEC * 1000) {
				console.error(
					`[AvigilonPlayer][${instanceId}] Starting watchdog timeout (${CONNECTION_TIMEOUT_SEC}s) - transitioning to Failed for camera=${camera?.connection?.cameraId}`
				);
				setStreamStatus("Failed");
				checkStreamCertAndSetMessage();
			}
		}, 1000);

		return () => clearInterval(watchdogInterval);
	}, [streamStatus]);

	// Provide detailed information about the current state of the Unity Player.
	const getCurrentStateOfUnityPlayer = event => {
		/** Note:
		 * First time when player is connected it plays live video.
		 * In the event of high latency this state change event is not invoked.
		 * So if state change is not received within 60 secs then we should allow user to retry connection.
		 */

		//To display retry button if time elapsed.
		lastStatusTimeRef.current = Date.now();

		const payload = event.detail.payload;
		const isRecordedMode = selectedVideoModeRef.current !== "Live";

		if (isRecordedMode) {
			console.debug(
				"Recorded video status:",
				`Connection: ${payload?.status?.network}`,
				`isLive: ${payload?.isLive}`,
				`isPlaying: ${payload?.status?.player}`,
				`Time: ${new Date(payload?.displayTime)}`
			);
		}

		if (payload?.isLive !== undefined && payload.isLive !== playerStatusRef.current?.isLive) {
			playerStatusRef.current.isLive = payload.isLive;
		}

		//player status: "playing", "paused", "stopped"
		if (payload?.status?.player && payload.status.player !== playerStatusRef.current?.player) {
			playerStatusRef.current.player = payload.status.player;
		}

		if (
			payload?.status?.network === "disconnected" &&
			payload.status.network !== playerStatusRef.current?.network
		) {
			if (isPageHiddenRef.current) {
				const now = Date.now();
				lastStatusTimeRef.current = now;
				if (streamStatus === "Started") {
					lastFrameTimeRef.current = now;
				}
				return;
			}

			playerStatusRef.current.network = "disconnected";
			playerStatusRef.current.player = payload.status.player;

			console.error(
				`[AvigilonPlayer][${instanceId}] Network disconnected for camera=${camera?.connection?.cameraId}`
			);
			// Reset frame clock so reconnect gets a fresh timeout window
			lastFrameTimeRef.current = null;
			skipNextPlayingEventRef.current = false;
			setFramesActive(false);
			setOverlayType(null);
			setStreamStatus("Starting");
			return;
		}

		if (payload?.status?.network === "connected") {
			if (payload.status.network !== playerStatusRef.current?.network) {
				playerStatusRef.current.network = "connected";
				// Set frame clock on first connect so the timeout starts from now
				if (lastFrameTimeRef.current === null) {
					lastFrameTimeRef.current = Date.now();
				}
				skipNextPlayingEventRef.current = true;
				setErrorMessage(undefined);
				setStreamStatus("Started");
			}
		}

		if (payload?.displayTime && payload.displayTime !== playerStatusRef.current?.displayTime) {
			playerStatusRef.current.displayTime = payload?.displayTime; //in ms

			// Treat advancing displayTime while actively playing as a "frame".
			// In recorded mode !isLive prevents early events from
			// masking a genuine no-start condition.
			// The first qualifying event after each play() call is skipped because
			// isPlaying can be sent as true prior to actual video data arrival.
			const isActivePlayback = payload.isPlaying && !payload.isScrubbing;
			const isRecordedConfirmed = !payload.isLive;
			if (isActivePlayback && (!isRecordedMode || isRecordedConfirmed)) {
				if (skipNextPlayingEventRef.current) {
					skipNextPlayingEventRef.current = false;
				} else {
					lastFrameTimeRef.current = Date.now();
					if (selectedVideoModeRef.current !== "Live") {
						firstFrameReceivedRef.current = true;
					}
					setFramesActive(true);
					setOverlayType(null);
				}
			}

			// Only process recorded-mode timeline logic when in recorded mode
			if (isRecordedMode) {
				// While isLive is true the displayTime is the live feed time, not
				// the recorded position. Ignore all recorded-mode logic until the
				// player confirms it has left live mode.
				if (!payload?.isLive) {
					EventBus.publish("setCurrentRecordedTimestamp", {
						instanceId,
						time: new Date(payload.displayTime)
					});

					// Publish load progress based on how far we are through the video range
					const startDt = videoStartDateTimeRef.current;
					const endDt = videoEndDateTimeRef.current;
					if (startDt && endDt) {
						const totalDuration = endDt - startDt;
						const elapsed = payload.displayTime - startDt;
						const progress =
							totalDuration > 0 ? Math.min(100, Math.max(0, (elapsed / totalDuration) * 100)) : 0;
						EventBus.publish("RecordedBottomBar_loadProgress", { instanceId, progress });

						// Publish loaded duration in seconds for TimeLineControls loaded bar
						const loadedDurationSecs = totalDuration > 0 ? totalDuration / 1000 : 0;
						EventBus.publish("loadedDurationChange", { instanceId, duration: loadedDurationSecs });
					}

					// Stop playback when displayTime exceeds videoEndDateTime
					if (endDt && payload.displayTime >= endDt) {
						const videoElem = videoElemRef.current;
						if (videoElem && playerStatusRef.current.player === "playing") {
							// If in clip mode, loop back to the start
							if (recordedModeRef.current === "clip" && startDt) {
								loopSeekToggleRef.current = !loopSeekToggleRef.current;
								const seekTime = startDt + (loopSeekToggleRef.current ? -1 : 1);
								videoElem.setPlaybackTime(seekTime);
								console.debug(
									`[AvigilonPlayer][${instanceId}] clip loop setPlaybackTime to: ${new Date(seekTime)}`
								);
								EventBus.publish("RecordedBottomBar_startVideo", { instanceId, playing: true });
							} else {
								userPausedRef.current = true;
								videoElem.pause();
								EventBus.publish("RecordedBottomBar_startVideo", { instanceId, playing: false });
							}
						}
					}
				}
			}
		}
	};

	const getConnectedVideoPlayer = () => {
		const videoElem = videoElemRef.current;
		if (!videoElem) return null;

		if (playerStatusRef.current.network === "disconnected") {
			videoElem.connect(connectionPayload);
		}

		return videoElem;
	};

	const playVideo = (playfromStart = false, epocTimeMillSecs = 0) => {
		const videoElem = getConnectedVideoPlayer();
		if (videoElem) {
			if (playfromStart) {
				console.debug(`[AvigilonPlayer][${instanceId}] setPlaybackTime to: ${new Date(epocTimeMillSecs)}`);
				videoElem.setPlaybackTime(epocTimeMillSecs);
			}
			userPausedRef.current = false;
			videoElem.play();
			videoElem.setAutoFocus();
		}
	};

	const pauseVideo = () => {
		const videoElem = getConnectedVideoPlayer();
		if (videoElem) {
			videoElem.pause(); // there is no stop() method.
			userPausedRef.current = true;
		}
	};

	const retryStream = () => {
		setTimeout(() => {
			setOverlayType(null);
			setErrorMessage(undefined);

			if (playerStatusRef.current.network !== "connected") {
				// Network lost — full reconnect path; interval restarts when "Started" arrives
				lastFrameTimeRef.current = null;
				firstFrameReceivedRef.current = false;
				setFramesActive(false);
				setStreamStatus("Starting");
				playVideo();
				return;
			}

			// Still connected, keep streamStatus "Started" so the frame-check interval keeps running, then re-attempt playback
			if (selectedVideoModeRef.current !== "Live") {
				playbackStartedRef.current = false;
				startRecordedPlayback();
			} else {
				firstFrameReceivedRef.current = false;
				lastFrameTimeRef.current = Date.now();
				skipNextPlayingEventRef.current = true;
				setFramesActive(false);
				playVideo();
			}
		}, STREAM_RETRY_DELAY_MS);
	};

	const streamFallbackHandler = strmStatus => {
		if (strmStatus === "Starting") {
			return createCenteredContainer(<CircularProgress data-testid="orion-components-cbcomponents-video-web-rtcplayer-avigilon-avigilon-web-rtcplayer-circular-progress" />);
		} else if (strmStatus === "Failed") {
			return <StreamFailed retryStream={retryStream} message={errorMessage} />;
		}

		return null;
	};

	const renderFrameErrorOverlay = () => {
		const baseStyle = {
			position: "absolute",
			top: 0,
			left: 0,
			right: 0,
			bottom: 0,
			display: "flex",
			alignItems: "center",
			justifyContent: "center"
		};

		if (overlayType) {
			let message;
			if (overlayType === "live-stall") {
				message = getTranslation("global.CBComponents.video.webRTCPlayer.liveStall");
			} else if (overlayType === "recorded-no-start") {
				message = getTranslation("global.CBComponents.video.webRTCPlayer.recordedNoStart");
			} else if (overlayType === "recorded-stall") {
				message = getTranslation("global.CBComponents.video.webRTCPlayer.recordedStall");
			} else {
				message = getTranslation("global.CBComponents.video.webRTCPlayer.camProblem");
			}

			return (
				<div style={{ ...baseStyle, background: "rgba(0,0,0,0.6)", zIndex: 2001 }}>
					<StreamFailed retryStream={retryStream} message={message} />
				</div>
			);
		}

		if (streamStatus === "Started" && !framesActive) {
			return (
				<div style={{ ...baseStyle, background: "rgba(0,0,0,0.3)", zIndex: 2 }}>
					<CircularProgress data-testid="orion-components-cbcomponents-video-web-rtcplayer-avigilon-avigilon-web-rtcplayer-circular-progress-2" />
				</div>
			);
		}

		return null;
	};

	const renderUnityPlayer = (
		instanceId,
		playerBaseUrl,
		cameraId,
		directMediaStreaming = true,
		mediaChannel = true
	) => {
		// Note: if no updates for more than CONNECTION_TIMEOUT_SEC then we should display StreamFailed for user to again connect.
		const isRecordedPaused = selectedVideoModeRef.current !== "Live" && userPausedRef.current;
		if (!overlayType && !isPageHiddenRef.current && !isRecordedPaused && lastStatusTimeRef.current !== null) {
			const nowTime = Date.now();
			const elapsed = nowTime - lastStatusTimeRef.current; // difference in ms
			if (elapsed > CONNECTION_TIMEOUT_SEC * 1000) {
				lastStatusTimeRef.current = nowTime;
				console.error(
					`[AvigilonPlayer][${instanceId}] Connection timeout (${CONNECTION_TIMEOUT_SEC}s) - no status updates received for camera=${cameraId}`
				);
				return streamFallbackHandler("Failed");
			}
		}

		return createCenteredContainer(
			<unity-player
				key={"player-" + instanceId}
				id={"player-" + instanceId}
				url={playerBaseUrl}
				camera-id={cameraId}
				direct-media-streaming={directMediaStreaming}
				media-channel={mediaChannel}
				player-type="bare-player"
				width="100%"
				height="100%"
				ref={videoElemRef}
			/>,
			{ width: "100%", height: "100%" }
		);
	};

	return (
		<Grid
			container
			spacing={isNewVideoPlayer ? 0 : 3}
			sx={{ width: "100%", height: "100%", position: "relative" }}
			className="mt-3"
		>
			{streamStatus !== "Started" && !overlayType && streamFallbackHandler(streamStatus)}
			{isLoaded && (
				<div
					style={{ display: streamStatus === "Started" ? undefined : "none", width: "100%", height: "100%" }}
				>
					{renderUnityPlayer(
						instanceId,
						baseUrl,
						camera?.connection?.cameraId || "",
						camera.player?.directMediaStreaming,
						camera.player?.mediaChannel
					)}
				</div>
			)}
			{renderFrameErrorOverlay()}
		</Grid>
	);
};

AvigilonWebRTCPlayer.propTypes = propTypes;
export default AvigilonWebRTCPlayer;
